import set from "lodash.set";
import { v4 } from "uuid";

import {
  QuantityButtonsUserInterface,
  UserInterfaceDto,
  WidgetCategory,
  WidgetType,
} from "@intelligems/ig-types";
import {
  createAction,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { useSelector } from "@src/store/hooks";
import { updateWidgetAfterPut } from "@src/store/slices/widgets";

import { IUserInterfacesState, UserInterface } from "./types";
import {
  getUserInterface,
  isProgressBarInterface,
  isQuantityButtonsInterface,
  updateUserInterfaceWidgets,
} from "./util";

const initialState: IUserInterfacesState = {
  interfaces: [],
  storedInterfaces: [],
};

const userInterfacesSlice = createSlice({
  name: "userInterfaces",
  initialState,
  reducers: {
    resetUserInterfaces: () => initialState,
    resetUserInterfacesForVariation: (
      draft,
      action: PayloadAction<{ variationId: string }>
    ) => {
      draft.interfaces = draft.interfaces.filter(
        (ui) => ui.variationId !== action.payload.variationId
      );
    },
    initUserInterfaces: (draft, action: PayloadAction<UserInterfaceDto[]>) => {
      draft.interfaces = action.payload;
      draft.storedInterfaces = draft.interfaces;
    },
    addUserInterface: (
      draft,
      action: PayloadAction<
        Partial<UserInterfaceDto> & {
          variationId: string;
        }
      >
    ) => {
      draft.interfaces.push({
        ...action.payload,
        id: action.payload.id ?? v4(),
        isEnabled: action.payload.isEnabled ?? true,
        isArchived: action.payload.isArchived ?? false,
        widgetId: action.payload.widget?.id ?? "",
        desktopVariables: action.payload.desktopVariables ?? {},
        mobileVariables: action.payload.mobileVariables ?? {},
        styleOverrides: action.payload.styleOverrides ?? null,
      });
    },
    updateUserInterface: (
      draft,
      action: PayloadAction<Partial<UserInterfaceDto> & { id: string }>
    ) => {
      const { id, ...update } = action.payload;
      const { interfaces } = draft;

      const index = interfaces.findIndex((i) => i.id === id);
      if (index > -1) {
        interfaces[index] = { ...interfaces[index], ...update };
      }
    },
    deleteQuantityButton: (
      draft,
      action: PayloadAction<{
        interfaceId: string;
        buttonIndex: number;
      }>
    ) => {
      const { interfaceId, buttonIndex } = action.payload;
      const userInterface = getUserInterface(draft, interfaceId);
      if (!isQuantityButtonsInterface(userInterface)) {
        return;
      }

      const buttons = getQuantityButtons(userInterface);
      buttons?.splice(buttonIndex, 1);
      userInterface.mobileVariables = userInterface.desktopVariables;
    },
    deleteProgressBarTier: (
      draft,
      action: PayloadAction<{
        interfaceId: string;
        offerTierId: string;
      }>
    ) => {
      const { interfaceId, offerTierId } = action.payload;
      const userInterface = getUserInterface(draft, interfaceId);
      if (!isProgressBarInterface(userInterface)) {
        return;
      }

      if (
        userInterface?.desktopVariables?.textReplacements?.tierVariables &&
        userInterface.desktopVariables.textReplacements.tierVariables[
          offerTierId
        ]
      ) {
        delete userInterface.desktopVariables.textReplacements.tierVariables[
          offerTierId
        ];
      }

      userInterface.mobileVariables = userInterface.desktopVariables;
    },
    addQuantityButton: (
      draft,
      action: PayloadAction<{
        interfaceId: string;
        buttonQuantity?: number;
        atBase?: boolean;
      }>
    ) => {
      const { interfaceId, atBase, buttonQuantity } = action.payload;
      const userInterface = getUserInterface(draft, interfaceId);
      if (!isQuantityButtonsInterface(userInterface)) {
        return;
      }

      const buttons = getQuantityButtons(userInterface);
      if (buttons.length > 0 && !atBase) {
        const last = buttons[buttons.length - 1];
        buttons.push({
          id: v4(),
          quantity:
            buttonQuantity !== undefined
              ? buttonQuantity
              : last.quantity
                ? last.quantity + 1
                : 1,
          unitName: last.unitName,
          buttonTitle: `${last.quantity + 1} items`,
        });
      } else {
        buttons.splice(0, 0, {
          id: v4(),
          quantity: buttonQuantity ?? 1,
          unitName: "item",
          buttonTitle: "1 item",
        });
      }
      userInterface.mobileVariables = userInterface.desktopVariables;
    },
    setUserInterfaceVariable: (
      draft: IUserInterfacesState,
      action: PayloadAction<{
        interfaceId: string;
        path: string;
        value: unknown;
      }>
    ) => {
      const { interfaceId, path, value } = action.payload;
      const userInterface = getUserInterface(draft, interfaceId);
      if (!userInterface) {
        return;
      }

      const desktopVariables =
        userInterface.desktopVariables ?? (userInterface.desktopVariables = {});
      set(desktopVariables, path, value);
      userInterface.mobileVariables = userInterface.desktopVariables;
    },
    resetGwpForQuantityButtons: (
      draft: IUserInterfacesState,
      action: PayloadAction<{
        interfaceId: string;
        buttonIndex: number;
      }>
    ) => {
      const { interfaceId, buttonIndex } = action.payload;
      const userInterface = getUserInterface(draft, interfaceId);
      if (!userInterface || !isQuantityButtonsInterface(userInterface)) {
        return;
      }

      const buttons = getQuantityButtons(userInterface);
      if (buttons[buttonIndex]?.freeGift) {
        buttons[buttonIndex].freeGift = {
          freeGiftLabel: null,
          freeGiftImage: null,
          freeGiftCompareAtPrice: null,
          showOriginalPrice: false,
          showOnlyWhenButtonSelected: false,
          hide: false,
          hideImage: false,
        };
      }
      userInterface.mobileVariables = userInterface.desktopVariables;
    },
    setUserInterfacesByVariationId: (
      draft,
      action: PayloadAction<{
        userInterfaces: UserInterfaceDto[];
        variationId: string;
      }>
    ) => {
      const { userInterfaces, variationId } = action.payload;
      const id = userInterfaces.find((i) => i.variationId === variationId)?.id;
      draft.interfaces = [
        ...draft.interfaces.filter((i) => i.variationId !== variationId),
        ...userInterfaces.map((i) => ({
          ...i,
          id: id || v4(),
          variationId,
          isEnabled: true,
          styleOverrides: i.styleOverrides ?? null,
        })),
      ] as UserInterface[];
    },
  },
  selectors: {
    getUserInterfaces: (state) => state.interfaces as UserInterface[],
    getStoredUserInterfaces: (state) =>
      state.storedInterfaces as UserInterface[],
  },
  extraReducers: (builder) => {
    builder.addCase(updateWidgetAfterPut, (state, action) => {
      updateUserInterfaceWidgets(state.interfaces, action.payload);
      updateUserInterfaceWidgets(state.storedInterfaces, action.payload);
    });
  },
});

export const {
  initUserInterfaces,
  resetUserInterfaces,
  resetUserInterfacesForVariation,
  addQuantityButton,
  deleteQuantityButton,
  addUserInterface,
  updateUserInterface,
  setUserInterfacesByVariationId,
  setUserInterfaceVariable,
  deleteProgressBarTier,
  resetGwpForQuantityButtons,
} = userInterfacesSlice.actions;

export const userInterfacesReducer = userInterfacesSlice.reducer;

export const { getUserInterfaces, getStoredUserInterfaces } =
  userInterfacesSlice.selectors;

export const createUserInterfaceWidget = createAction<{
  id: string;
  type: WidgetType;
}>("userInterfaces/createUserInterfaceWidget");

function getQuantityButtons(userInterface: QuantityButtonsUserInterface) {
  const variables =
    userInterface.desktopVariables ?? (userInterface.desktopVariables = {});
  return variables.buttons ?? (variables.buttons = []);
}

export const useUserInterfaceWithStyles = (
  variationId: string,
  widgetType: WidgetType
) => {
  return useSelector((state) => {
    const userInterfaces = getUserInterfaces(state);
    return (
      userInterfaces.find(
        (ui) =>
          ui.variationId === variationId && ui.widget?.widgetType === widgetType
      ) || null
    );
  });
};
